import { ChangeDetectorRef, Component, OnDestroy, OnInit, Output, ViewEncapsulation } from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import { BehaviorSubject } from "rxjs";
import { Channel } from "../../../channels/models/channel.model";
import { ChannelService } from "../../../channels/channel.service";
import { ConstantsUtil } from "../../utils/constants.util";
import { combineLatest, Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, filter, map, startWith, switchMap, take, takeUntil } from "rxjs/operators";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { Broadcaster } from "../../shared/providers";
import {
  REMOVE_CHILD_ARCHIVED_TOPICS,
  REMOVE_PARENT_ARCHIVED_CHANNEL
} from "../archive-window-topic/archive-window-topic.component";
import { ToastService } from "../../../shared/services/toast.service";
import { DEFAULT_AVATAR } from "../../../common";
import { ChannelRepository } from "app/channels/repository/channel.repository";
import { ChannelSnackbarService } from "app/channels/channel-snackbar.service";
import { TranslateService } from "@ngx-translate/core";
import { SnackbarType } from "app/channels/models/snackbar.model";
import { CommonUtil } from "../../utils/common.util";
import { HostListener } from "@angular/core";
    import { TalkRootState } from "app/talk/reducers";
import { Store } from "@ngrx/store";
import { CommonService } from "app/shared/providers";
import { DatetimeService } from "app/talk/services/datetime.service";
import { getAppSettings } from "app/reducers";
import format from "date-fns/format";
import de from "date-fns/locale/de";
import enUS from "date-fns/locale/en-US";
import { AppSettings } from "app/talk/models/app-settings.model";

@Component({
  selector: "vp-archive-window-channels",
  templateUrl: "./archive-window-channels.component.html",
  styleUrls: ["./archive-window-channels.component.scss"],
  encapsulation: ViewEncapsulation.None
})
export class ArchiveWindowChannelsComponent implements OnInit, OnDestroy {

  constructor(private _channelService: ChannelService,
    private _dialog: MatDialog,
    private router: Router,
    private _broadcaster: Broadcaster,
    private _toastService: ToastService,
    private channelSnackBarService: ChannelSnackbarService,
    private translate: TranslateService,
    private changeDetectorRef: ChangeDetectorRef,
    private channelRepo: ChannelRepository,
    private dateTimeService: DatetimeService,
    private commonService: CommonService,
    private store: Store<TalkRootState>) { }

  @Output() totalChannels = new BehaviorSubject<number>(0);
  groupType = ChannelGroupType;
  searchControl = new UntypedFormControl("");
  groupControl = new UntypedFormControl(this.groupType.DATE_ASC);
  channels = new BehaviorSubject<Channel[]>([]);
  data = { offset: 0, limit: ConstantsUtil.PAGINATION_LIMIT.ARCHIVED_CHANNELS, q: "", sort: ChannelGroupType.DATE_ASC };
  private isAlive$ = new Subject<boolean>();
  isRightBarExpanded: boolean = false;
  isLeftBarExpanded: boolean = false;
  channelsGroupedByAuthor = {};
  defaultAvatar = DEFAULT_AVATAR;
  authorsGroup = [];
  dismissLoader: boolean = false;
  lang = "en";
  settings: AppSettings;

  ngOnInit(): void {
    this.commonService.currentLanguage.pipe(takeUntil(this.isAlive$)).subscribe((lang: string) => {
      this.lang = lang;
    });
    this.store.select(getAppSettings).pipe(takeUntil(this.isAlive$)).subscribe(options => {
      this.settings = options;
      this.changeDetectorRef.markForCheck();
    });
    this.channelRepo.getRightbarExpanded().pipe(takeUntil(this.isAlive$)).subscribe((res: boolean) => {
      this.isRightBarExpanded = res;
      this.changeDetectorRef.markForCheck();
    });

    this.channelRepo.getIsLeftSideBarExpanded().pipe(takeUntil(this.isAlive$)).subscribe((res: boolean) => {
      this.isLeftBarExpanded = res;
      this.changeDetectorRef.markForCheck();
    });
    this.fetchArchivedChannels();
    this.listenSearchChanges();
    this.listenBroadcasterChanges();
    this.channelRepo.getArchiveChannelsSortBy().pipe(takeUntil(this.isAlive$)).subscribe(sortBy => {
      if (!!sortBy) {
        this.groupControl.setValue(sortBy);
      } else {
        this.groupControl.setValue(this.groupType.DATE_ASC);
      }
    });
    this.groupControl.valueChanges.subscribe(sortBy => {
      this.channelRepo.setArchiveChannelsSortBy(sortBy);
    });
  }

  @HostListener("document:wheel", ["$event.target"])
  onScroll(): void {
    this.loadMoreChannels();
  }

  ngOnDestroy(): void {
    this.isAlive$.next(false);
    this.isAlive$.complete();
  }

  fetchArchivedChannels() {
    this.data.offset = 0;
    this.totalChannels.next(0);
    this.channels.next([]);
    this.getArchivedChannels()
      .pipe(takeUntil(this.isAlive$))
      .subscribe(channels => this.setArchivedChannels(channels));
  }

  loadMoreChannels() {
    const loadedChannels = [...(this.channels.getValue() || [])].length;
    const totalChannels = this.totalChannels.getValue();
    if (loadedChannels < totalChannels) {
      this.getArchivedChannels().subscribe((channels: Channel[]) => this.setArchivedChannels(channels));
    }
  }

  async openChannelParticipantsPopup(channel: Channel) {
    const { ChannelParticipantsComponent } = await import(
      "../../channel-participants/channel-participants.component");
    this._dialog.open(ChannelParticipantsComponent, {
      backdropClass: "vnctalk-form-backdrop",
      panelClass: "vnctalk-form-panel",
      width: "440px",
      height: "640px",
      autoFocus: true,
      data: { channel }
    });
  }

  async unArchiveChannel(channel: Channel) {
    const { UnarchiveChannelComponent } = await import(
      "../unarchive-channel/unarchive-channel.component");
    const unArchiveChannelRef = this._dialog.open(UnarchiveChannelComponent, {
      backdropClass: "vnctalk-form-backdrop",
      panelClass: "vnctalk-form-panel",
      width: "480px",
      height: "336px",
      autoFocus: true,
      data: { channelName: channel.name }
    });
    unArchiveChannelRef.afterClosed()
      .pipe(take(1), filter(val => val?.hasOwnProperty("unArchiveTopics")))
      .subscribe(value => {
        this.unarchiveChannelConfirmed(channel, value?.unArchiveTopics);
      });
  }

  private unarchiveChannelConfirmed(channel: Channel, unArchiveTopics = true, showSnackbar = true) {
    this._channelService.unarchiveChannel(channel.id, unArchiveTopics).subscribe(res => {
      if (res?.channel) {
        this.channelRepo.addChannelIntoStore(res?.channel);
        this.channelRepo.setChannelCategory(res?.channel);
        if (showSnackbar) {
          this.translate.get("UNARCHIVE_CHANNEL_SUCCESS").subscribe(text => this.channelSnackBarService.openSnackBar(text, SnackbarType.CHECKMARK));
        }
        this.removeChannelLocally(channel?.id);
        if (unArchiveTopics) {
          this.removeChildArchivedTopics(channel?.id);
        }
      }
    });
  }

  private updateCount() {
    const count = this.totalChannels.value;
    this.totalChannels.next(count - 1);
  }

  private removeChannelLocally(channelId: string) {
    let channels = this.channels.value;
    channels = channels.filter(c => c.id !== channelId);
    this.channels.next(channels);
    this.updateCount();
  }

  private listenBroadcasterChanges() {
    this._broadcaster.on(REMOVE_PARENT_ARCHIVED_CHANNEL).pipe(takeUntil(this.isAlive$)).subscribe((res: any) => {
      setTimeout(() => {
        this.unarchiveChannelConfirmed(res?.channel, false, false);
        // this.removeChannelLocally(res?.channel?.id);
        // this.channelRepo.addChannelIntoStore(res?.channel);
        // this.channelRepo.changeChannelCategory(res?.channel, 'archived');
      });
    });
  }

  onOpenChannel(item) {
    this.router.navigateByUrl(`/talk/channels/${item.id}`);
  }
  showChannelInfo(item, event?) {
    if (event) this.onOpenChannel(item);
    else this.channelRepo.openSideBar("channel", item.id);
  }

  private removeChildArchivedTopics(channelId) {
    this._broadcaster.broadcast(REMOVE_CHILD_ARCHIVED_TOPICS, { channelId: channelId });
  }

  private getArchivedChannels() {
    return this._channelService.getArchivedChannels(this.data).pipe(takeUntil(this.isAlive$), map(value => {
      let newOffset = (value?.offset || 0) + ConstantsUtil.PAGINATION_LIMIT.ARCHIVED_CHANNELS;
      const total_count = value?.total_count || 0;
      newOffset = newOffset > total_count ? total_count : newOffset;
      this.totalChannels.next(total_count);
      this.dismissLoader = true;
      this.data.offset = newOffset;
      return (value?.channels || []);
    }));
  }

  private listenSearchChanges() {
    const $searchControlOb = this.searchControl.valueChanges.pipe(debounceTime(500), distinctUntilChanged(), startWith(""));
    const $groupControlOb = this.groupControl.valueChanges.pipe(startWith(this.groupType.DATE_ASC));
    combineLatest([$searchControlOb, $groupControlOb])
      .pipe(takeUntil(this.isAlive$), switchMap((value: [string, ChannelGroupType]) => {
        this.data.q = value[0] || "";
        this.data.sort = value[1] || ChannelGroupType.DATE_ASC;
        this.data.offset = 0;
        this.totalChannels.next(0);
        this.channels.next([]);
        return this.getArchivedChannels();
      })).subscribe((channels: Channel[]) => {
        this.setArchivedChannels(channels);
      });
  }

  private setArchivedChannels(channels: Channel[]) {
    const filterAndConcat = (oldChannels: Channel[], newChannels: Channel[]) => {
      const resultantChannels = [...oldChannels];
      newChannels.forEach(newChannel => {
        const index = oldChannels.findIndex(oldChannel => oldChannel.id === newChannel.id);
        if (index === -1) {
          resultantChannels.push(newChannel);
        }
      });
      return resultantChannels;
    };
    channels.map(item => {
      item["avatar_url"] = item?.default_cover ? CommonUtil.getVncDirectoryAttachemntURL(item?.default_cover) : item?.avatar_url;
    });
    this.channels.next(filterAndConcat(this.channels.getValue(), channels));
    this.getChannelsGroupedByAuthor();
  }

  getChannelsGroupedByAuthor() {
    this.channelsGroupedByAuthor = {};
    this.authorsGroup = [];
    const currentChannelsResponse = this.channels.getValue();
    currentChannelsResponse.forEach((channel: Channel) => {
      const authorEmailId = channel?.author?.jid;
      if (!this.channelsGroupedByAuthor.hasOwnProperty(authorEmailId)) {
        this.authorsGroup.push(authorEmailId);
        this.channelsGroupedByAuthor[authorEmailId] = {
          authorData: { ...channel?.author },
          channels: []
        };
      }
      this.channelsGroupedByAuthor?.[authorEmailId].channels.push(channel);
    });
  }

  async permanentlyDeleteChannel(channel: Channel) {
    let options: any = {
      width: "480px",
      height: "280px"
    };
    if (CommonUtil.isMobileSize()) {
      options = {
        maxWidth: "100vw",
        maxHeight: "100vh",
        minHeight: "300px"
      };
    }
    const { ConfirmationChannelComponent } = await import(
      "../../../channels/confirmation-channel/confirmation-channel.component");
    this._dialog.open(ConfirmationChannelComponent, Object.assign({
      backdropClass: "vnctalk-form-backdrop",
      panelClass: ["vnctalk-form-panel", "confirmation-dialog"],
      disableClose: true,
      data: {
        headerText: "DELETE_CHANNEL",
        bodyText: "DELETE_CHANNEL_MSG",
        okLabel: "DELETE"
      },
      autoFocus: true
    }, options)).afterClosed().pipe(take(1)).subscribe((res: any) => {
      if (!!res && res.confirmation) {
        if (res.confirmation === "yes") {
          this.channelRepo.deleteChannel(channel.id, true, false).subscribe();
          let tempChannels = this.channels.getValue();
          tempChannels = tempChannels.filter(eachChannel => eachChannel.id !== channel.id);
          this.channels.next(tempChannels);
          this.totalChannels.next(tempChannels.length);
        }
      }
    });
  }

  getDateTimeInFormat(timestamp: any): string {
    if (!!timestamp) {
      const formatStr = this.dateTimeService.getFormatString(this.lang);
      const time = format(new Date(timestamp), formatStr, { locale: this.lang === "de" ? de : enUS });
      return time.split(" ")[0];
    }
    return null;
  }

}

export const REMOVE_CHILD_DELETED_TOPICS = "REMOVE_CHILD_ARCHIVED_TOPICS";
export enum ChannelGroupType {
  "DATE_ASC" = "created_on:asc",
  "DATE_DESC" = "created_on:desc",
  "AUTHOR_ASC" = "author:asc",
  "AUTHOR_DESC" = "author:desc"
}
